import React, { useState, useEffect, useCallback } from 'react';
import { playSound } from '../utils/useSound';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import './PatinhasSlots.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// --- CONFIGURAÇÃO DO JOGO ---
const SYMBOLS = [
    { id: 1, icon: '🪙', label: 'Moeda', value: 0.5, weight: 45 },
    { id: 2, icon: '💵', label: 'Dólar', value: 1, weight: 30 },
    { id: 3, icon: '💰', label: 'Dinheiro', value: 3, weight: 15 },
    { id: 4, icon: '💎', label: 'Joia', value: 10, weight: 8 },
    { id: 5, icon: '🦆', label: 'Pato', value: 50, weight: 2 }
];

const PAYLINES = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8], 
    [0, 4, 8], [2, 4, 6]
];

const getRandomSymbol = () => {
    const totalWeight = SYMBOLS.reduce((acc, symbol) => acc + symbol.weight, 0);
    let randomNum = Math.random() * totalWeight;
    for (let i = 0; i < SYMBOLS.length; i++) {
        if (randomNum < SYMBOLS[i].weight) return SYMBOLS[i];
        randomNum -= SYMBOLS[i].weight;
    }
    return SYMBOLS[0];
};

const PatinhasSlots = () => {
    const STARTING_BALANCE = 1000;
    const [balance, setBalance] = useState(STARTING_BALANCE);
    const [betAmount, setBetAmount] = useState(10);
    const [grid, setGrid] = useState(Array(9).fill(SYMBOLS[0]));
    const [spinningCols, setSpinningCols] = useState([false, false, false]);
    const [winMessage, setWinMessage] = useState('Multiplique sua fortuna!');
    const [patinhasMood, setPatinhasMood] = useState('neutral');
    const [balanceHistory, setBalanceHistory] = useState([STARTING_BALANCE]);
    const [winningCells, setWinningCells] = useState([]);
    
    // Estados para Estatisticas
    const [stats, setStats] = useState({
        totalWins: 0,
        totalLosses: 0,
        biggestWin: 0,
        totalGainedLost: 0,
        roundsPlayed: 0
    });

    useEffect(() => {
        // Pré-carrega os sons se necessário
    }, []);

    const isAnySpinning = spinningCols.some(Boolean);

    useEffect(() => {
        if (balance <= 0) setPatinhasMood('shocked');
    }, [balance]);

    useEffect(() => {
        if (!isAnySpinning) return;

        const interval = setInterval(() => {
            setGrid(currentGrid => currentGrid.map((symbol, i) => {
                if (spinningCols[i % 3]) return getRandomSymbol();
                return symbol;
            }));
        }, 30);

        return () => clearInterval(interval);
    }, [isAnySpinning, spinningCols]);

    const spin = useCallback(() => {
        if (balance < betAmount || isAnySpinning) return;

        setBalance(prev => prev - betAmount);
        setWinMessage('GIRANDO...');
        setPatinhasMood('neutral');
        setWinningCells([]);
        setSpinningCols([true, true, true]);
        playSound('spinStart');

        const finalGrid = Array.from({ length: 9 }, () => getRandomSymbol());
        const STOP_DELAY = 500; 
        const STAGGER = 300;    

        setTimeout(() => {
            setSpinningCols([false, true, true]);
            setGrid(current => {
                const newGrid = [...current];
                [0, 3, 6].forEach(i => newGrid[i] = finalGrid[i]);
                return newGrid;
            });
            playSound('reelStop');
        }, STOP_DELAY);

        setTimeout(() => {
            setSpinningCols([false, false, true]);
            setGrid(current => {
                const newGrid = [...current];
                [1, 4, 7].forEach(i => newGrid[i] = finalGrid[i]);
                return newGrid;
            });
            playSound('reelStop');
        }, STOP_DELAY + STAGGER);

        setTimeout(() => {
            setSpinningCols([false, false, false]);
            setGrid(finalGrid);
            playSound('reelStop');
            finalizeRound(finalGrid);
        }, STOP_DELAY + STAGGER * 2);

    }, [balance, betAmount, isAnySpinning]);

    const finalizeRound = (finalGrid) => {
        let roundWin = 0;
        let newWinningCells = [];
        let nearMiss = false;

        PAYLINES.forEach(line => {
            const s1 = finalGrid[line[0]];
            const s2 = finalGrid[line[1]];
            const s3 = finalGrid[line[2]];

            if (s1.id === s2.id && s2.id === s3.id) {
                roundWin += betAmount * s1.value;
                newWinningCells.push(...line);
            } 
            else if (s1.id === s2.id || s2.id === s3.id || s1.id === s3.id) {
                nearMiss = true;
            }
        });

        if (roundWin > 0) {
            setBalance(prev => {
                const newBal = prev + roundWin;
                setBalanceHistory(h => [...h, newBal]);
                return newBal;
            });
            setWinningCells([...new Set(newWinningCells)]);
            
            setStats(prev => ({
                ...prev,
                totalWins: prev.totalWins + 1,
                biggestWin: Math.max(prev.biggestWin, roundWin),
                totalGainedLost: prev.totalGainedLost + (roundWin - betAmount),
                roundsPlayed: prev.roundsPlayed + 1
            }));
            
            if (roundWin < betAmount) {
                setWinMessage(`Bateu na trave! Recuperou R$ ${roundWin.toFixed(2)}`);
                setPatinhasMood('neutral'); 
            } else if (roundWin > betAmount * 10) {
                 setWinMessage(`💰 SUPER BIG WIN! R$ ${roundWin.toFixed(2)} 💰`);
                 setPatinhasMood('happy');
                 playSound('bigWin');
            } else {
                 setWinMessage(`VITÓRIA! Ganhou R$ ${roundWin.toFixed(2)}!`);
                 setPatinhasMood('happy');
                 playSound('smallWin');
            }
        } else {
            setBalanceHistory(h => [...h, balance]);
            
            setStats(prev => ({
                ...prev,
                totalLosses: prev.totalLosses + 1,
                totalGainedLost: prev.totalGainedLost - betAmount,
                roundsPlayed: prev.roundsPlayed + 1
            }));
            
            if (nearMiss) {
                setWinMessage('UUH! Foi quase!');
                setPatinhasMood('shocked');
                playSound('nearMiss');
            } else {
                setWinMessage('A casa venceu essa.');
                setPatinhasMood('angry');
                playSound('loss');
            }
        }
    };

    const runSimulation = () => {
        if (isAnySpinning) return;
        let simBal = balance;
        let simHist = [...balanceHistory];
        
        for(let i=0; i<100; i++) {
            if(simBal < betAmount) break;
            simBal -= betAmount;
            const g = Array.from({ length: 9 }, () => getRandomSymbol());
            PAYLINES.forEach(line => {
                 if (g[line[0]].id === g[line[1]].id && g[line[1]].id === g[line[2]].id) {
                     simBal += betAmount * g[line[0]].value;
                 }
            });
            simHist.push(simBal);
        }
        setBalance(simBal);
        setBalanceHistory(simHist);
        setWinMessage('Simulação rápida finalizada.');
    };

    // --- FUNÇÃO EXPORTAR CSV MELHORADA ---
    const exportToCSV = () => {
        // 1. Adiciona BOM (\uFEFF) para o Excel reconhecer acentuação
        let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
        
        // Helper para formatar números: troca ponto por vírgula (padrão BR)
        const fmt = (num) => num.toFixed(2).replace('.', ',');

        // 2. Usa ponto e vírgula (;) como separador
        csvContent += "RESUMO ESTATISTICO\n";
        csvContent += `Rodadas Jogadas;${stats.roundsPlayed}\n`;
        csvContent += `Vitorias;${stats.totalWins}\n`;
        csvContent += `Derrotas;${stats.totalLosses}\n`;
        csvContent += `Maior Premio;${fmt(stats.biggestWin)}\n`;
        csvContent += `Lucro/Prejuizo Total;${fmt(stats.totalGainedLost)}\n`;
        csvContent += `Saldo Final;${fmt(balance)}\n\n`;

        // Histórico Detalhado
        csvContent += "HISTORICO DE SALDO\n";
        csvContent += "Rodada;Saldo (R$)\n";
        balanceHistory.forEach((bal, index) => {
            csvContent += `${index};${fmt(bal)}\n`;
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "patinhas_dados.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const chartData = {
        labels: balanceHistory.map((_, i) => i),
        datasets: [{
            label: 'Saldo (R$)',
            data: balanceHistory,
            borderColor: '#00ff00',
            backgroundColor: 'rgba(0, 255, 0, 0.1)',
            tension: 0.2,
            pointRadius: 0,
            borderWidth: 2
        }]
    };

    return (
        <div className="patinhas-container">
            <div className="main-layout">
                
                <div className="side-panel left-panel">
                    <h3>Estatísticas</h3>
                    <div className="stat-item">
                        <span className="stat-label">Rodadas:</span>
                        <span className="stat-value">{stats.roundsPlayed}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Vitórias:</span>
                        <span className="stat-value win-color">{stats.totalWins}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Derrotas:</span>
                        <span className="stat-value loss-color">{stats.totalLosses}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Maior Vitória:</span>
                        <span className="stat-value">R$ {stats.biggestWin.toFixed(2)}</span>
                    </div>
                    <div className="stat-item total">
                        <span className="stat-label">L/P Total:</span>
                        <span className={`stat-value ${stats.totalGainedLost >= 0 ? 'win-color' : 'loss-color'}`}>
                            R$ {stats.totalGainedLost.toFixed(2)}
                        </span>
                    </div>
                </div>

                <div className="game-area">
                    <div className={`patinhas-avatar mood-${patinhasMood}`}>
                        {patinhasMood === 'neutral' && '🦆'}
                        {patinhasMood === 'happy' && '🤑'}
                        {patinhasMood === 'angry' && '😤'}
                        {patinhasMood === 'shocked' && '😱'}
                    </div>

                    <div className="slots-grid">
                        {grid.map((symbol, i) => {
                            const isColSpinning = spinningCols[i % 3];
                            return (
                                <div key={i} className={`grid-cell ${winningCells.includes(i) ? 'winner' : ''} ${isColSpinning ? 'spinning' : 'stopped'}`}>
                                    <span className="symbol-icon">{symbol.icon}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="hud">
                        <div className="win-message">{winMessage}</div>
                        <div className="balance-box">
                            SALDO: <span style={{color: balance < 100 ? 'red' : '#00ff00'}}>R$ {balance.toFixed(2)}</span>
                        </div>
                        
                        <div className="controls">
                            <div className="bet-control">
                                <span>Aposta:</span>
                                <select value={betAmount} onChange={(e) => setBetAmount(Number(e.target.value))} disabled={isAnySpinning}>
                                    <option value={10}>R$ 10</option>
                                    <option value={50}>R$ 50</option>
                                    <option value={100}>R$ 100</option>
                                </select>
                            </div>
                            <button className="spin-btn" onClick={spin} disabled={isAnySpinning || balance < betAmount}>
                                {isAnySpinning ? '...' : 'GIRAR!'}
                            </button>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                            <button className="sim-btn" onClick={runSimulation} disabled={isAnySpinning}>
                                ⏩ Avançar 100 Rodadas
                            </button>
                            
                            <button 
                                className="sim-btn" 
                                onClick={exportToCSV} 
                                disabled={isAnySpinning}
                                style={{ background: '#555', color: '#eee' }}
                            >
                                📥 Exportar Dados (CSV)
                            </button>
                        </div>
                    </div>
                </div>

                <div className="side-panel right-panel">
                    <h3>Sua Jornada Financeira</h3>
                    <div className="chart-box">
                        <Line 
                            data={chartData} 
                            options={{ 
                                responsive: true, 
                                maintainAspectRatio: false, 
                                plugins: { legend: {display: false} }, 
                                scales: { 
                                    x: {display:false}, 
                                    y: {
                                        grid: {color: '#333'},
                                        ticks: { color: '#aaa', font: {size: 10} } 
                                    } 
                                } 
                            }} 
                        />
                    </div>
                    <div className="paytable-inline">
                        {SYMBOLS.map(s => (
                            <span key={s.id} className="paytable-item">
                                <span className="paytable-icon">{s.icon}</span> x3 = {s.value}x
                            </span>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PatinhasSlots;